import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Scale, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import api from '@/services/api';

const AuthPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', name: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', loginData);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success(t('loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('Google login')) {
        toast.error(t('loginGoogleOnly'));
      } else {
        toast.error(t('loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', registerData);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success(t('registerSuccess'));
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('already registered')) {
        toast.error(t('registerEmailExists'));
      } else {
        toast.error(t('registerError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#060B14] via-[#0C1527] to-[#060B14] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E6C687] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">{t('authProcessing')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#060B14] via-[#0C1527] to-[#060B14] flex items-center justify-center px-6 py-12 relative">
      {/* Language Selector */}
      <div className="absolute top-6 right-6">
        <LanguageSelector variant="header" />
      </div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Scale className="w-10 h-10 text-[#E6C687]" />
            <span className="text-3xl font-bold gradient-text">Law Study A.I</span>
          </div>
          <p className="text-gray-400">{t('landingSubtitle')}</p>
        </div>

        {/* Auth Form */}
        <div className="glass-effect p-8 rounded-2xl border border-white/10">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/5">
              <TabsTrigger data-testid="tab-login" value="login" className="data-[state=active]:bg-[#E6C687] data-[state=active]:text-[#060B14]">
                <LogIn className="w-4 h-4 mr-2" />
                {t('login')}
              </TabsTrigger>
              <TabsTrigger data-testid="tab-register" value="register" className="data-[state=active]:bg-[#E6C687] data-[state=active]:text-[#060B14]">
                <UserPlus className="w-4 h-4 mr-2" />
                {t('register')}
              </TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-gray-300">{t('email')}</Label>
                  <Input
                    data-testid="login-email-input"
                    id="login-email"
                    type="email"
                    placeholder={t('email')}
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    required
                    className="mt-2 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-gray-300">{t('password')}</Label>
                  <Input
                    data-testid="login-password-input"
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    required
                    className="mt-2 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E6C687] text-[#060B14] hover:bg-[#F3D89F] font-semibold"
                >
                  {loading ? t('loading') : t('login')}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[#060B14] text-gray-400">{t('orSeparator')}</span>
                </div>
              </div>

              <Button
                data-testid="google-login-btn"
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('loginWithGoogle')}
              </Button>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-gray-300">{t('name')}</Label>
                  <Input
                    data-testid="register-name-input"
                    id="register-name"
                    type="text"
                    placeholder={t('name')}
                    value={registerData.name}
                    onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                    required
                    className="mt-2 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-gray-300">{t('email')}</Label>
                  <Input
                    data-testid="register-email-input"
                    id="register-email"
                    type="email"
                    placeholder={t('email')}
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    required
                    className="mt-2 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-gray-300">{t('password')}</Label>
                  <Input
                    data-testid="register-password-input"
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    required
                    className="mt-2 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E6C687] text-[#060B14] hover:bg-[#F3D89F] font-semibold"
                >
                  {loading ? t('loading') : t('register')}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[#060B14] text-gray-400">{t('orSeparator')}</span>
                </div>
              </div>

              <Button
                data-testid="google-register-btn"
                type="button"
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('loginWithGoogle')}
              </Button>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Button
              data-testid="back-to-home-btn"
              variant="link"
              onClick={() => navigate('/')}
              className="text-[#E6C687] hover:text-[#F3D89F]"
            >
              {t('backToHome')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;